import gatewayClient from "../../services/api/gateway.js";
import { downloadFile } from "../../services/api/download.js";

const BASE = "/agents/scoping";
const enc = (v) => encodeURIComponent(String(v));

export const scopingApi = {
  createRun: async (payload) =>
    (await gatewayClient.post(`${BASE}/runs`, payload)).data,
  listRuns: async () => (await gatewayClient.get(`${BASE}/runs`)).data,
  getRun: async (id) => (await gatewayClient.get(`${BASE}/runs/${id}`)).data,
  deleteRun: async (id) =>
    (await gatewayClient.delete(`${BASE}/runs/${id}`)).data,
  snapshotCandidates: async (tenantId) =>
    (
      await gatewayClient.get(`${BASE}/snapshot-candidates`, {
        params: { tenant_id: tenantId },
      })
    ).data,
  getQuestionSets: async () =>
    (await gatewayClient.get(`${BASE}/question-sets`)).data,
  collect: async (id, body = {}) =>
    (await gatewayClient.post(`${BASE}/runs/${id}/collect`, body)).data,
  saveSignals: async (id, body) =>
    (await gatewayClient.post(`${BASE}/runs/${id}/signals`, body)).data,
  // Reference documents (T4) — the whole list, overwritten wholesale (brief-edits
  // idiom): removing a doc is the caller omitting it from the next PUT.
  setReferenceDocs: async (id, docs) =>
    (await gatewayClient.put(`${BASE}/runs/${id}/reference-docs`, { docs }))
      .data,
  runBrief: async (id) =>
    (await gatewayClient.post(`${BASE}/runs/${id}/brief`)).data,
  // Hand edits ONLY — never the generated brief. `edits` is the whole map, keyed by
  // briefEdits.opportunityKey(generated title) plus the reserved EXEC_SUMMARY_KEY, and
  // the PUT overwrites it wholesale (TSA's sow-edits save model): reverting one
  // opportunity is the caller omitting its key from the next map, not a second call.
  // The old full-brief `PUT /runs/{id}/brief` route is gone — it overwrote the generated
  // column, which is what made Regenerate destroy every hand edit.
  saveBriefEdits: async (id, edits) =>
    (await gatewayClient.put(`${BASE}/runs/${id}/brief-edits`, { edits })).data,
  runExport: async (id) =>
    (await gatewayClient.post(`${BASE}/runs/${id}/export`)).data,
  // PSA handoff — preview and confirm share one URL and are told apart by the
  // body's `confirm` flag, so both are pinned by contract tests. Preview never
  // touches Autotask; confirm creates the project and stores the link.
  psaProjectPreview: async (id, idx) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/opportunities/${idx}/psa-project`,
        { confirm: false },
      )
    ).data,
  psaProjectCreate: async (id, idx, { title, description } = {}) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/opportunities/${idx}/psa-project`,
        { confirm: true, title, description },
      )
    ).data,
  // PSA ticket handoff (Task 7) — clones the project pair exactly, `psa-ticket` in place
  // of `psa-project`. Preview never touches Autotask; confirm creates the ticket and
  // stores the link.
  psaTicketPreview: async (id, idx) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/opportunities/${idx}/psa-ticket`,
        { confirm: false },
      )
    ).data,
  psaTicketCreate: async (id, idx, { title, description } = {}) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/opportunities/${idx}/psa-ticket`,
        { confirm: true, title, description },
      )
    ).data,
  handoffTsa: async (id, idx) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/opportunities/${idx}/handoff-tsa`,
      )
    ).data,
  // Actions stage — project groups. The PUT overwrites the whole group list (brief-edits
  // idiom) and returns the stored `project_groups`, links included: links are
  // server-owned, so only key/title/members are sent. The group PSA routes tell preview
  // from confirm by the body's `confirm` flag, like the per-opportunity pair above.
  putProjectGroups: async (id, groups) =>
    (await gatewayClient.put(`${BASE}/runs/${id}/project-groups`, { groups }))
      .data,
  groupPsaProject: async (id, key, { confirm = false } = {}) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/project-groups/${enc(key)}/psa-project`,
        { confirm },
      )
    ).data,
  groupPsaAttach: async (id, key, { project_id, confirm = false }) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/project-groups/${enc(key)}/psa-attach`,
        { project_id, confirm },
      )
    ).data,
  // The run's Autotask company's open projects for the attach picker:
  // `{projects: [{id, name, status, agent_created}]}`, agent-created first.
  companyProjects: async (id) =>
    (await gatewayClient.get(`${BASE}/runs/${id}/company-projects`)).data,
  groupHandoffTsa: async (id, key) =>
    (
      await gatewayClient.post(
        `${BASE}/runs/${id}/project-groups/${enc(key)}/handoff-tsa`,
      )
    ).data,
  // Option B landing aggregate: severity_counts + audits[] (top signals, AM,
  // Autotask link, effort roll-up) + tracker_top. See overview.build_overview.
  getOverview: async () => (await gatewayClient.get(`${BASE}/overview`)).data,
  // Signal Tracker (Task 8): the full derived funnel — {totals, signals[], tiers}.
  // Cached server-side for 10 minutes (see tracker.compute_tracker); the same
  // cache backs overview.tracker_top, so this call is cheap even on a cold page.
  getTracker: async () => (await gatewayClient.get(`${BASE}/tracker`)).data,
  // PSA status mapping (Task 6): the values GET is a live Autotask-MCP proxy (id/label/
  // isActive per Projects `status` picklist); the map GET/PUT is the admin-configured
  // approved/declined id sets. PUT is admin-gated server-side (_require_admin_key) —
  // the gateway forwards X-API-Key for this path the same way it does for
  // /api/settings*'s dashboard PUT (see settingsApi.js), so the SPA itself never holds
  // the key.
  getPsaStatusValues: async () =>
    (await gatewayClient.get(`${BASE}/psa-status-values`)).data,
  getPsaStatusMap: async () =>
    (await gatewayClient.get(`${BASE}/psa-status-map`)).data,
  savePsaStatusMap: async ({ approved, declined }) =>
    (await gatewayClient.put(`${BASE}/psa-status-map`, { approved, declined }))
      .data,
  // Signals Catalogue (Task 9): every active rule stripped to display fields
  // {rule_key, name, description, severity, category, source_platform, priority}
  // plus {counts} for the legend chips (info exposed as positive). See
  // routes._public_rule / routes._severity_counts on the backend.
  getRules: async () => (await gatewayClient.get(`${BASE}/rules`)).data,
  getFleet: async () => (await gatewayClient.get(`${BASE}/fleet`)).data,
  startFleetSweep: async () =>
    (await gatewayClient.post(`${BASE}/fleet/sweep`)).data,
  deepSweepCustomer: async (customerId) =>
    (
      await gatewayClient.post(`${BASE}/fleet/customers/${customerId}/sweep`, {
        depth: "deep",
      })
    ).data,
  // /agents/scoping* is behind the gateway's feature gate: an artifact is a blob fetch through
  // the bearer client (services/api/download.js), never an <a href> to the gateway URL.
  downloadArtifact: (id, kind) =>
    downloadFile(`${BASE}/runs/${enc(id)}/artifacts/${enc(kind)}`, { fallbackName: `audit-request-${id}-${kind}` }),
};
